import { AlarmClock, Eye, EyeOff, Globe, ThumbsDown, ThumbsUp } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Etiqueta } from '@/features/tickets/badges';
import {
  ALCANCE_DE_VISIBILIDADE,
  diasAteRevisao,
  EFEITO_DE_SITUACAO,
  revisaoVencida,
  ROTULO_DE_SITUACAO,
  ROTULO_DE_VISIBILIDADE,
  type SituacaoDeArtigo,
  type Visibilidade,
} from '@/features/knowledge/tipos';

/** As etiquetas da base de conhecimento. */

const ESTILO_DE_SITUACAO: Record<SituacaoDeArtigo, string> = {
  DRAFT: 'bg-muted text-muted-foreground ring-border',
  IN_REVIEW: 'bg-warning/15 text-warning ring-warning/30',
  APPROVED: 'bg-primary/12 text-primary ring-primary/25',
  PUBLISHED: 'bg-success/12 text-success ring-success/25',
  ARCHIVED: 'bg-muted text-muted-foreground ring-border',
};

export function EtiquetaDeSituacao({ situacao }: { situacao: SituacaoDeArtigo }) {
  return (
    <Etiqueta className={ESTILO_DE_SITUACAO[situacao]} title={EFEITO_DE_SITUACAO[situacao]}>
      {ROTULO_DE_SITUACAO[situacao]}
    </Etiqueta>
  );
}

/**
 * ★ A etiqueta de público.
 *
 * Não é decoração. Escolher "Cliente" num procedimento que cita senha de
 * firewall é o erro mais caro desta fase, e uma etiqueta discreta faria a
 * pessoa não olhar. Por isso INTERNO e CLIENTE têm cores diferentes e o
 * `title` diz quem alcança — o que a escolha FAZ, não o nome dela.
 */
const ESTILO_DE_VISIBILIDADE: Record<Visibilidade, string> = {
  INTERNAL: 'bg-secondary text-secondary-foreground ring-border',
  CUSTOMER: 'bg-warning/15 text-warning ring-warning/30',
  PUBLIC: 'bg-destructive/12 text-destructive ring-destructive/25',
};

const ICONE_DE_VISIBILIDADE: Record<Visibilidade, typeof Eye> = {
  INTERNAL: EyeOff,
  CUSTOMER: Eye,
  PUBLIC: Globe,
};

export function EtiquetaDeVisibilidade({ visibilidade }: { visibilidade: Visibilidade }) {
  const Icone = ICONE_DE_VISIBILIDADE[visibilidade];
  return (
    <Etiqueta
      className={ESTILO_DE_VISIBILIDADE[visibilidade]}
      title={ALCANCE_DE_VISIBILIDADE[visibilidade]}
    >
      <Icone className="mr-1 h-3 w-3" aria-hidden />
      {ROTULO_DE_VISIBILIDADE[visibilidade]}
    </Etiqueta>
  );
}

/**
 * A validade do artigo.
 *
 * Conhecimento sem validade vira folclore, e o leitor precisa saber que o que
 * está lendo venceu — é a única defesa contra seguir um procedimento que
 * deixou de valer. Artigo em dia com folga não vira etiqueta: um aviso que
 * aparece sempre deixa de ser aviso.
 */
export function EtiquetaDeRevisao({
  nextReviewAt,
  agora,
}: {
  nextReviewAt: string | null;
  agora?: Date;
}) {
  const dias = diasAteRevisao(nextReviewAt, agora);
  if (dias === null || dias > 30) return null;
  const vencida = revisaoVencida(nextReviewAt, agora);
  return (
    <Etiqueta
      className={
        vencida
          ? 'bg-destructive/12 text-destructive ring-destructive/25'
          : 'bg-warning/15 text-warning ring-warning/30'
      }
      title={
        vencida
          ? 'A data de revisão passou. O artigo continua no ar — tirá-lo puniria quem lê pelo atraso de quem revisa — mas o conteúdo pode estar desatualizado.'
          : 'A revisão está próxima.'
      }
    >
      <AlarmClock className="mr-1 h-3 w-3" aria-hidden />
      {vencida ? `Revisão vencida há ${Math.abs(dias)} d` : `Revisar em ${dias} d`}
    </Etiqueta>
  );
}

/**
 * As métricas de utilidade.
 *
 * `resolvedCount` vem primeiro porque é o número que decide se o artigo vale a
 * pena — "quantos chamados este texto fechou" é outra pergunta, e melhor, do
 * que "quantas vezes foi aberto". Zero não vira etiqueta.
 */
export function Utilidade({
  resolvidos,
  usos,
  uteis,
  inuteis,
}: {
  resolvidos: number;
  usos: number;
  uteis: number;
  inuteis: number;
}) {
  if (usos <= 0) return null;
  return (
    <span className="inline-flex items-center gap-2 text-xs text-muted-foreground">
      <span
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 font-semibold ring-1 ring-inset',
          resolvidos >= 5
            ? 'bg-success/12 text-success ring-success/25'
            : 'bg-secondary text-secondary-foreground ring-border',
        )}
        title="Chamados que este artigo resolveu, entre os que o consultaram. O contador é mantido pelo banco, por trigger."
      >
        {resolvidos}/{usos} resolvidos
      </span>
      {uteis > 0 && (
        <span className="inline-flex items-center gap-1" title="Avaliaram como útil">
          <ThumbsUp className="h-3 w-3" aria-hidden />
          {uteis}
        </span>
      )}
      {inuteis > 0 && (
        <span className="inline-flex items-center gap-1" title="Avaliaram como não útil">
          <ThumbsDown className="h-3 w-3" aria-hidden />
          {inuteis}
        </span>
      )}
    </span>
  );
}

/**
 * O corpo do artigo.
 *
 * `dangerouslySetInnerHTML` é seguro **porque o servidor higieniza na escrita**
 * (`sanitizeRichText`, lista de permissão) — e não porque o texto pareça
 * confiável. A dependência é essa, e está escrita aqui para que ninguém
 * remova a higienização do outro lado achando que a tela se protege sozinha.
 */
export function CorpoDoArtigo({ html, className }: { html: string; className?: string }) {
  return (
    <div
      className={cn(
        'prose-conhecimento max-w-none text-sm leading-relaxed text-foreground',
        '[&_h2]:mb-2 [&_h2]:mt-6 [&_h2]:text-base [&_h2]:font-semibold',
        '[&_h3]:mb-2 [&_h3]:mt-4 [&_h3]:text-sm [&_h3]:font-semibold',
        '[&_p]:mb-3 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5',
        '[&_li]:mb-1 [&_a]:text-primary [&_a]:underline',
        '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs',
        '[&_pre]:mb-3 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3',
        '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic',
        '[&_table]:mb-3 [&_table]:w-full [&_table]:border-collapse',
        '[&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1',
        '[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-2 [&_th]:py-1 [&_th]:text-left',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
